import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';

import useAuth from '../../../auth/useAuth';

import './styles.css';

import Layout from '../../UI/introGameLayout/layout';

import ProcessReviewSession from '../../UI/instructor/onboarding/reviewSession/process';

import {
  coursesQuery,
  tournamentsQuery,
  tournamentsRoundsQuery,
  tournamentsTeamsQuery,
  setupTournament
} from './api';

import { roundNames, teamNames } from './data';

// Default round → game-type order. When a tournament has fewer than 4
// games we take the first N from this list that the instructor created.
// Types are stored as the short codes created in onboarding's
// gameTypeMapping (ACC/NOR/BE/PR).
const DEFAULT_GAME_ORDER = ['ACC', 'NOR', 'BE', 'PR'];

export default function InstructorOnboardingReviewSessionAllSet() {
  const navigate = useNavigate();
  const { getTournamentGames } = useAuth();

  const shuffledTeamNames = [...teamNames].sort(() => Math.random() - 0.5);

  const [latestTournament, setLatestTournament] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { data: courses } = useQuery(coursesQuery());

  const { data: tournaments } = useQuery(tournamentsQuery('all-set'));
  const { data: tournamentsRounds } = useQuery(tournamentsRoundsQuery({tournamentId: latestTournament?.id}));
  const { data: tournamentsTeams } = useQuery(tournamentsTeamsQuery({tournamentId: latestTournament?.id}));

  const setupTournamentMutation = useMutation({
    mutationFn: (data) => setupTournament(data)
  });

  useEffect(() => {
    if(courses?.length > 0 && tournaments?.length > 0) {
      const courseId = courses[0].id;

      const courseTournaments = tournaments.filter(tournament => tournament.course_id === courseId);
      const latestTournament = courseTournaments.reduce((max, tournament) => tournament.id > max.id ? tournament : max, courseTournaments[0]);

      setLatestTournament(latestTournament);
    }
  }, [courses, tournaments]);

  // Block browser-back from leaving this page. The tournament has
  // already been created at this point and the prior step (game-type
  // selection) shouldn't be revisited — going back would let the
  // instructor re-fire the create-tournament flow on top of one that
  // already exists. Push a sentinel history entry on mount; when the
  // user hits Back, popstate fires and we immediately push another
  // duplicate so the URL stays on /all-set.
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleViewButtonClick = async () => {
    if (!latestTournament || !tournamentsRounds?.length || !tournamentsTeams?.length) return;
    setIsLoading(true);

    const tournamentRoundNames = roundNames[latestTournament.round_count] || [];

    // Assign each round to the corresponding game in DEFAULT_GAME_ORDER.
    // Games the instructor didn't create are skipped, so round 1 still
    // goes to Classification, round 2 to Normal Balance, etc. — just
    // collapsed to however many games are actually available.
    const tournamentGames = getTournamentGames() || [];
    const orderedGames = DEFAULT_GAME_ORDER
      .map(type => tournamentGames.find(g => g.type === type))
      .filter(Boolean);

    const rounds = tournamentsRounds
      .map((round, i) => {
        const game = orderedGames[i];
        const roundPayload = { id: round.id, name: tournamentRoundNames[i] };
        if (game) roundPayload.game_id = game.id;
        return roundPayload;
      })
      .filter(r => r.name);

    const teams = tournamentsTeams
      .map((team, i) => ({ id: team.id, name: shuffledTeamNames[i] }))
      .filter(t => t.name);

    await setupTournamentMutation.mutateAsync({
      tournamentId: latestTournament.id,
      rounds,
      teams,
      assignStudents: true,
    });

    navigate('/instructor/onboarding-review-session/competition');
  }

  return (
    <Layout
      isHeaderVisible={false}
      isContentVertical={true}
      backgroundColor={'linear-gradient(135deg, #EC5E59 2.88%, rgba(146, 219, 225, 0.8) 99.03%)'}
      className="instructor-onboarding-layout"
    >
      <ProcessReviewSession
        isButtonLoading={isLoading}
        onFinish={handleViewButtonClick}
        latestTournament={latestTournament}
      />
    </Layout>
  );
}
